import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

export function selectModelRecommendation(variants, model) {
  if (!model || !Array.isArray(variants)) return null;
  const matches = variants.filter((variant) => variant.model === model && variant.id && variant.url && variant.image?.src);
  return matches.find((variant) => variant.available) ?? matches[0] ?? null;
}

class ProductRecommendations extends Component {
  #activated = false;
  #generation = 0;
  #variantGeneration = 0;
  #selectionPending = false;
  #cachedRecommendations = new Map();
  #activeFetch = null;
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#activated = true;
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );
  #mutationObserver = new MutationObserver(() => {
    if (this.#activated) this.#loadRecommendations();
  });

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-intent', 'data-section-id'],
    });
    if (this.dataset.recommendationsPerformed === 'true') {
      this.#activated = true;
      if (this.querySelector('[data-recommendation-variants]')) this.#renderRecommendations(this.outerHTML);
      else this.#loadRecommendations();
    } else {
      this.#intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#variantGeneration++;
    this.#activeFetch?.abort();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  #onProductSelect = async (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    if (!picker || picker.closest('product-card, dialog, quick-add-dialog')) return;
    if (picker.dataset.productId !== this.dataset.productId) return;

    const generation = ++this.#variantGeneration;
    this.#generation++;
    this.#selectionPending = true;
    if (this.#activated) this.hidden = true;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#variantGeneration) return;
      this.#selectionPending = false;
      const index = Number.parseInt(this.dataset.modelOptionIndex ?? '', 10);
      const model = Number.isInteger(index) ? detail?.resource?.options?.[index] : null;
      this.dataset.selectedModel = typeof model === 'string' ? model : '';
      if (detail?.newProduct) this.dataset.productId = String(detail.newProduct.id);
      if (!this.dataset.selectedModel) {
        this.hidden = true;
        return;
      }
      if (this.#activated) this.#loadRecommendations();
    } catch {
      if (generation === this.#variantGeneration) {
        this.#selectionPending = false;
        this.hidden = true;
      }
    }
  };

  async #loadRecommendations() {
    const { productId, sectionId, intent, selectedModel } = this.dataset;
    const generation = ++this.#generation;
    if (this.#selectionPending || !productId || !sectionId || !selectedModel) {
      this.hidden = true;
      return;
    }
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent || 'related');
    const key = url.toString();
    let markup = this.#cachedRecommendations.get(key);
    this.setAttribute('aria-busy', 'true');
    try {
      if (!markup) {
        this.#activeFetch?.abort();
        const controller = new AbortController();
        this.#activeFetch = controller;
        const response = await fetch(key, { signal: controller.signal });
        if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
        markup = await response.text();
        this.#cachedRecommendations.set(key, markup);
        if (this.#activeFetch === controller) this.#activeFetch = null;
      }
      if (!this.isConnected || generation !== this.#generation) return;
      this.#renderRecommendations(markup);
    } catch (error) {
      if (generation !== this.#generation || error?.name === 'AbortError') return;
      this.hidden = true;
    } finally {
      if (generation === this.#generation) this.removeAttribute('aria-busy');
    }
  }

  #renderRecommendations(markup) {
    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
    const incoming = documentFragment.getElementById(this.id);
    if (!incoming) {
      this.hidden = true;
      return;
    }
    const model = this.dataset.selectedModel;
    const limit = Number.parseInt(this.dataset.maxProducts ?? '4', 10) || 4;
    let count = 0;

    for (const list of incoming.querySelectorAll('.resource-list')) {
      let listCount = 0;
      for (const item of list.querySelectorAll('[data-model-recommendation]')) {
        let variant = null;
        try {
          const data = JSON.parse(item.querySelector('[data-recommendation-variants]')?.textContent || '[]');
          variant = selectModelRecommendation(data, model);
        } catch {
          variant = null;
        }
        const template = item.querySelector('template[data-recommendation-card]');
        if (!variant || !(template instanceof HTMLTemplateElement) || listCount >= limit) {
          item.closest('slideshow-slide')?.remove();
          item.remove();
          continue;
        }
        const card = template.content.cloneNode(true);
        const link = card.querySelector('[data-recommendation-link]');
        const image = card.querySelector('[data-recommendation-image]');
        const label = card.querySelector('[data-recommendation-variant]');
        const price = card.querySelector('[data-recommendation-price]');
        const badge = card.querySelector('[data-recommendation-unavailable]');
        if (!link || !image || !label || !price || !badge) {
          item.closest('slideshow-slide')?.remove();
          item.remove();
          continue;
        }
        const variantUrl = new URL(variant.url, window.location.origin);
        if (variantUrl.origin !== window.location.origin) {
          item.closest('slideshow-slide')?.remove();
          item.remove();
          continue;
        }
        variantUrl.searchParams.set('variant', variant.id);
        link.href = `${variantUrl.pathname}${variantUrl.search}${variantUrl.hash}`;
        image.src = variant.image.src;
        image.alt = variant.image.alt || '';
        image.width = variant.image.width || 900;
        image.height = variant.image.height || 1200;
        label.textContent = variant.title;
        price.textContent = variant.price;
        badge.hidden = Boolean(variant.available);
        item.replaceChildren(card);
        item.dataset.recommendedModel = model;
        item.dataset.recommendedVariantId = variant.id;
        listCount++;
      }
      for (const slide of list.querySelectorAll('slideshow-slide')) {
        if (!slide.querySelector('[data-model-recommendation]')) slide.remove();
      }
      for (const slideshow of list.querySelectorAll('slideshow-component')) {
        const slides = [...slideshow.querySelectorAll('slideshow-slide')];
        slides.forEach((slide, index) => {
          slide.setAttribute('aria-hidden', String(index !== 0));
          slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
        });
        slideshow.style.setProperty('--slideshow-timeline', slides.map((_, index) => `--slide-${index}`).join(','));
      }
      list.dataset.hasRecommendations = String(listCount > 0);
      count = Math.max(count, listCount);
    }

    const stylesheet = documentFragment.querySelector('style[data-section-stylesheet]');
    const section = this.closest('.shopify-section');
    if (stylesheet && section) {
      const existingStylesheet = section.querySelector('style[data-section-stylesheet]');
      if (existingStylesheet) existingStylesheet.textContent = stylesheet.textContent;
      else section.prepend(stylesheet);
    }
    this.replaceChildren(...incoming.childNodes);
    this.dataset.recommendationsPerformed = 'true';
    this.hidden = count === 0;
    this.updatedCallback();
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
